import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_INTERVAL = 60000; // 1 minuto
const CHECK_INTERVAL = 30000; // 30 segundos

export function useSession() {
  const [sessionLocked, setSessionLocked] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Atualizar última atividade
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Verificar se sessão é válida
  const checkSession = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSessionLocked(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/auth/verificar?token=${token}`);
      
      if (!response.data.valid) {
        if (response.data.reason === "SESSION_TIMEOUT") {
          // Sessão expirou por inatividade - mostrar tela de bloqueio
          setSessionLocked(true);
          setUsuario({
            nome: response.data.usuario_nome,
            email: response.data.usuario_email
          });
        } else if (response.data.reason === "INVALID_TOKEN") {
          // Token inválido - fazer logout
          localStorage.removeItem("token");
          localStorage.removeItem("usuario");
          localStorage.removeItem("empresa");
          localStorage.removeItem("permissoes");
          window.location.href = "/login";
        }
      } else {
        setSessionLocked(false);
        setUsuario(response.data.usuario);
      }
    } catch (error) {
      console.error("Erro ao verificar sessão:", error);
    }
  }, []);

  // Enviar heartbeat para manter sessão ativa
  const sendHeartbeat = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await axios.post(`${API}/api/auth/heartbeat`, null, {
        params: { token }
      });
    } catch (error) {
      console.error("Erro no heartbeat:", error);
    }
  }, []);

  // Logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    localStorage.removeItem("empresa");
    localStorage.removeItem("permissoes");
    window.location.href = "/login";
  }, []);

  // Unlock screen
  const unlockScreen = useCallback(() => {
    setSessionLocked(false);
    updateActivity();
    sendHeartbeat();
  }, [updateActivity, sendHeartbeat]);

  // Inicializar listeners de atividade
  useEffect(() => {
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Verificar sessão periodicamente
    checkIntervalRef.current = setInterval(checkSession, CHECK_INTERVAL);
    
    // Enviar heartbeat periodicamente quando há atividade
    heartbeatIntervalRef.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      // Só envia heartbeat se houve atividade nos últimos 2 minutos
      if (timeSinceActivity < 120000) {
        sendHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);

    // Verificar sessão imediatamente
    checkSession();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    };
  }, [updateActivity, checkSession, sendHeartbeat]);

  return {
    sessionLocked,
    usuario,
    unlockScreen,
    handleLogout,
    checkSession
  };
}
